import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import { Navbar } from "@/components/Navbar";
import AuthCallback from "@/components/AuthCallback";
import Home from "@/pages/Home";
import Search from "@/pages/Search";
import ListingDetail from "@/pages/ListingDetail";
import CreateListing from "@/pages/CreateListing";
import SellerProfile from "@/pages/SellerProfile";
import Dashboard from "@/pages/Dashboard";
import Messages from "@/pages/Messages";
import Login from "@/pages/Login";
import AdminDashboard from "@/pages/AdminDashboard";
import BoostListing from "@/pages/BoostListing";
import BecomeDealer from "@/pages/BecomeDealer";
import DealerImport from "@/pages/DealerImport";
import { PaymentSuccess, PaymentCancel } from "@/pages/PaymentResult";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";

const NotFound = () => {
  const navigate = useNavigate();
  return (
    <div className="max-w-lg mx-auto px-6 py-28 text-center">
      <div className="font-heading font-900 text-6xl text-blaze spec-block">404</div>
      <h1 className="font-heading font-900 text-2xl uppercase tracking-tight mt-4">Page not found</h1>
      <p className="text-sm text-muted-foreground mt-2">That route doesn't exist — it may have been removed.</p>
      <Button onClick={() => navigate("/")} className="mt-6 bg-blaze hover:bg-blaze-hover text-white rounded-sm font-bold">Back to marketplace</Button>
    </div>
  );
};

const ProtectedRoute = ({ children, adminOnly }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-blaze border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/" replace />;
  return children;
};

const Shell = ({ children }) => (
  <div className="min-h-screen grain">
    <Navbar />
    {children}
  </div>
);

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Shell>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/search" element={<Search />} />
        <Route path="/listing/:id" element={<ListingDetail />} />
        <Route path="/seller/:id" element={<SellerProfile />} />
        <Route path="/login" element={<Login />} />
        <Route path="/sell" element={<ProtectedRoute><CreateListing /></ProtectedRoute>} />
        <Route path="/edit/:id" element={<ProtectedRoute><CreateListing /></ProtectedRoute>} />
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/messages" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
        <Route path="/messages/:id" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
        <Route path="/boost/:id" element={<ProtectedRoute><BoostListing /></ProtectedRoute>} />
        <Route path="/dealer" element={<ProtectedRoute><BecomeDealer /></ProtectedRoute>} />
        <Route path="/dealer/import" element={<ProtectedRoute><DealerImport /></ProtectedRoute>} />
        <Route path="/admin" element={<ProtectedRoute adminOnly><AdminDashboard /></ProtectedRoute>} />
        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/payment/cancel" element={<PaymentCancel />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Shell>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster position="top-right" theme="dark" offset="80px" />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
